import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import './pages.css';

const UserProfile = () => {
  const [userData, setUserData] = useState(null);
  const [bookedEvents, setBookedEvents] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sectionParam = queryParams.get('section');
  const [activeSection, setActiveSection] = useState(sectionParam || 'profile');

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (!session || sessionError) {
        navigate('/login');
        return;
      }
      const { data, error } = await supabase.from('users').select('*').eq('id', session.user.id).single();
      if (error) console.error('Error fetching profile:', error.message);
      else setUserData(data);
    };
    fetchProfile();
  }, [navigate]);

  // Fetch booked events
  useEffect(() => {
    if (!userData) return;
    const fetchBookedEvents = async () => {
      const { data, error } = await supabase
        .from('ticket_sales')
        .select('*, events(*)')
        .eq('user_id', userData.id);

      if (error) console.error('Error fetching booked events:', error.message);
      else {
        const cleanedData = data
          .filter(row => row.events)
          .map(row => ({ ...row.events, invoice_id: row.invoice_id }));
        setBookedEvents(cleanedData);
      }
    };
    fetchBookedEvents();
  }, [userData]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleDownloadTicket = async (event) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [210, 99],
    });

    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.rect(10, 10, 190, 79);

    const titleText = event.tittle ? event.tittle.toUpperCase() : 'EVENT';
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(titleText, 20, 25);

    doc.setLineWidth(0.3);
    doc.setDrawColor(150);
    doc.line(20, 28, 190, 28);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Date:', 20, 40);
    doc.text(event.date || '', 50, 40);
    doc.text('Location:', 20, 48);
    doc.text(event.location || '', 50, 48);
    doc.text('Attendee:', 20, 56);
    doc.text(userData.full_name || '', 50, 56);
    doc.text('Invoice ID:', 20, 64);
    doc.text(event.invoice_id || '—', 50, 64);
    doc.text('Amount Paid:', 20, 72);
    doc.text(`Ksh ${event.price}`, 50, 72);

    doc.setLineWidth(0.1);
    doc.setDrawColor(100);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(150, 10, 150, 89);

    const qrText = `ticket-${event.id}-${userData.id}`;
    try {
      const qrDataUrl = await QRCode.toDataURL(qrText);
      doc.addImage(qrDataUrl, 'PNG', 155, 30, 40, 40);
    } catch (err) {
      console.error('Error generating QR code:', err);
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Please present this ticket at the entrance.', 20, 85);

    doc.save(`${titleText}-ticket.pdf`);
  };

  if (!userData) return <p>Loading profile...</p>;

  return (
    <div className="profile-layout">
      <aside className="profile-sidebar">
        <div>
          <div className="user-name">{userData.full_name}</div>
          <nav className="sidebar-menu">
            <div className={`sidebar-link${activeSection === 'profile' ? ' active' : ''}`} onClick={() => setActiveSection('profile')}>👤 Profile</div>
            <div className={`sidebar-link${activeSection === 'events' ? ' active' : ''}`} onClick={() => setActiveSection('events')}>🎫 Events</div>
            <div className={`sidebar-link${activeSection === 'billing' ? ' active' : ''}`} onClick={() => setActiveSection('billing')}>💳 Billing</div>
            <div className="sidebar-footer">
              <button className="logout-btn" onClick={handleLogout}>Log Out ↩</button>
            </div>
          </nav>
        </div>
      </aside>

      <main className="profile-content">
        {activeSection === 'profile' && (
          <>
            <h2>My Profile</h2>
            <form className="profile-form" onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target;
              const full_name = form.full_name.value;
              const email = form.email.value;
              const phone = form.phone.value;
              const newPassword = form.new_password.value;

              const { error: updateError } = await supabase
                .from('users')
                .update({ full_name, email, phone })
                .eq('id', userData.id);

              if (updateError) return alert('Error updating profile: ' + updateError.message);

              if (newPassword) {
                const { error: pwError } = await supabase.auth.updateUser({ password: newPassword });
                if (pwError) return alert('Error updating password: ' + pwError.message);
              }

              alert('Profile updated successfully!');
            }}>
              <label>Full Name <input type="text" name="full_name" defaultValue={userData.full_name} /></label>
              <label>Email <input type="email" name="email" defaultValue={userData.email} /></label>
              <label>Phone Number <input type="tel" name="phone" defaultValue={userData.phone} /></label>
              <label>Change Password <input type="password" name="new_password" placeholder="New Password" /></label>
              <button type="submit" className="submit-btn">Save Changes</button>
            </form>
          </>
        )}

        {activeSection === 'events' && (
          <>
            <h2>My Events</h2>
            {bookedEvents.length === 0 ? (
              <p>You have not booked any events yet.</p>
            ) : (
              <div className="event-cards-container">
                {bookedEvents.map((event) => (
                  <div key={event.id} className="event-card">
                    <img src={event.image_url} alt={event.tittle} className="event-image" />
                    <div className="event-info">
                      <h3>{event.tittle}</h3>
                      <p><strong>Date:</strong> {event.date}</p>
                      <p><strong>Location:</strong> {event.location}</p>
                      <Link to={`/event/${event.id}`} className="event-link">View Event</Link>
                      <button onClick={() => handleDownloadTicket(event)} className="btn download-btn">🎟️ Download Ticket</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeSection === 'billing' && (
          <div className="billing-section">
            <h2>Billing & Transactions</h2>
            {bookedEvents.length === 0 ? (
              <p>No transactions found.</p>
            ) : (
              <div className="billing-table-container">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Event</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookedEvents.map((event, index) => (
                      <tr key={event.id + '-bill'}>
                        <td>{index + 1}</td>
                        <td>{event.tittle}</td>
                        <td>{event.date}</td>
                        <td>Ksh {event.price}</td>
                        <td><span className="status-badge paid">Paid</span></td>
                        <td>{event.invoice_id || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default UserProfile;
